import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";

/**
 * Category-specific copy for the Field Note form.
 *
 * The backend stores the SAME six fields for every category (duration,
 * price_paid, would_do_again, would_do_again_reason, downside, not_good_for)
 * — we only vary the labels + placeholders so that the questions actually
 * make sense for what the seed is. Adding a new category = one entry here.
 *
 * Rule for the shape: every entry must supply the four label strings and
 * the four placeholders below. Missing keys fall back to `default`.
 */
const PROMPTS = {
  default: {
    heading: "Your Field Note",
    duration_label: "How long have you owned / used it?",
    duration_placeholder: "e.g. 8 months",
    price_label: "What did you pay?",
    price_placeholder: "e.g. $42, or 'made from pantry staples'",
    again_label: "Would you buy / do it again?",
    reason_placeholder: "Because…",
    downside_label: "What's the downside?",
    downside_placeholder: "Something that would have been useful to know",
    not_for_label: "Who is this NOT good for?",
    not_for_placeholder: "Be specific about who should skip this",
  },
  "Travel/Places": {
    heading: "Your Field Note from this place",
    duration_label: "When did you visit and how long were you there?",
    duration_placeholder: "e.g. Long weekend in May 2024",
    price_label: "Roughly what did the trip cost?",
    price_placeholder: "e.g. $180 for the weekend, or 'free — day trip'",
    again_label: "Would you go back?",
    reason_placeholder: "Because…",
    downside_label: "What surprised you or would have been useful to know before going?",
    downside_placeholder: "Parking, timing, crowds, seasonality…",
    not_for_label: "Who shouldn't bother going?",
    not_for_placeholder: "Be honest about who would find it a letdown",
  },
};

export default function FieldNoteForm({ ideaId, saved, onCreated, category }) {
  const p = PROMPTS[category] || PROMPTS.default;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    duration: "",
    price_paid: "",
    would_do_again: "yes",
    would_do_again_reason: "",
    downside: "",
    not_good_for: "",
  });

  if (!saved) {
    return (
      <div data-testid="save-first-hint" className="mt-6 p-5 rounded-2xl border border-dashed border-[#B79256]/50 text-[#2A2823]/70">
        Save this seed to your Saved shelf first — Field Notes come from people who actually engaged with the idea.
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        would_do_again: form.would_do_again === "yes",
      };
      const { data } = await api.post(`/ideas/${ideaId}/field-notes`, payload);
      onCreated(data);
      setOpen(false);
      setForm({ duration: "", price_paid: "", would_do_again: "yes", would_do_again_reason: "", downside: "", not_good_for: "" });
      if (data?.advanced_streak && data?.streak_weeks) {
        const n = data.streak_weeks;
        const msg =
          n === 1
            ? "Field Note added. Streak started — see you next week."
            : n === 4
              ? `Field Note added. 4-week streak — brass badge is yours.`
              : n > 4
                ? `Field Note added. ${n}-week streak — still growing.`
                : `Field Note added. ${n}-week streak building.`;
        toast(msg, { duration: 5000 });
      } else {
        toast("Field Note added.");
      }
    } catch (err) {
      toast(err?.response?.data?.detail || "Could not save Field Note.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <Button
        data-testid="open-field-note-btn"
        onClick={() => setOpen(true)}
        className="mt-6 rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] px-6 h-11 shadow-none"
      >
        Write a Field Note
      </Button>
    );
  }

  return (
    <form onSubmit={submit} className="mt-6 rounded-3xl border border-[#B79256]/30 bg-white p-6 space-y-5" data-testid="field-note-form">
      <h3 className="font-serif-display text-2xl text-[#2A2823]">{p.heading}</h3>

      <div>
        <Label className="accent-label text-[#B79256]">{p.duration_label}</Label>
        <Input data-testid="fn-duration" required value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} className="mt-2 rounded-xl border-[#B79256]/40 bg-[#FBF8F1] focus-visible:ring-[#4E5A3D]" placeholder={p.duration_placeholder} />
      </div>
      <div>
        <Label className="accent-label text-[#B79256]">{p.price_label}</Label>
        <Input data-testid="fn-price" required value={form.price_paid} onChange={(e) => setForm({ ...form, price_paid: e.target.value })} className="mt-2 rounded-xl border-[#B79256]/40 bg-[#FBF8F1]" placeholder={p.price_placeholder} />
      </div>

      <div>
        <Label className="accent-label text-[#B79256]">{p.again_label}</Label>
        <RadioGroup
          value={form.would_do_again}
          onValueChange={(v) => setForm({ ...form, would_do_again: v })}
          className="mt-3 flex gap-6"
        >
          <label className="flex items-center gap-2 cursor-pointer">
            <RadioGroupItem data-testid="fn-again-yes" value="yes" className="border-[#4E5A3D] text-[#4E5A3D]" /> Yes
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <RadioGroupItem data-testid="fn-again-no" value="no" className="border-[#4E5A3D] text-[#4E5A3D]" /> No
          </label>
        </RadioGroup>
        <Textarea
          data-testid="fn-reason"
          required
          value={form.would_do_again_reason}
          onChange={(e) => setForm({ ...form, would_do_again_reason: e.target.value })}
          className="mt-3 rounded-xl border-[#B79256]/40 bg-[#FBF8F1]"
          placeholder={p.reason_placeholder}
          rows={3}
        />
      </div>

      <div>
        <Label className="accent-label text-[#B79256]">{p.downside_label}</Label>
        <Textarea data-testid="fn-downside" required value={form.downside} onChange={(e) => setForm({ ...form, downside: e.target.value })} className="mt-2 rounded-xl border-[#B79256]/40 bg-[#FBF8F1]" rows={2} placeholder={p.downside_placeholder} />
      </div>

      <div>
        <Label className="accent-label text-[#B79256]">{p.not_for_label}</Label>
        <Textarea data-testid="fn-not-for" required value={form.not_good_for} onChange={(e) => setForm({ ...form, not_good_for: e.target.value })} className="mt-2 rounded-xl border-[#B79256]/40 bg-[#FBF8F1]" rows={2} placeholder={p.not_for_placeholder} />
      </div>

      <div className="flex gap-3">
        <Button data-testid="fn-submit" type="submit" disabled={busy} className="rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] px-6 h-11 shadow-none">
          {busy ? "Planting…" : "Post Field Note"}
        </Button>
        <Button type="button" onClick={() => setOpen(false)} variant="outline" className="rounded-full border-[#B79256]/50 text-[#2A2823] bg-transparent hover:bg-[#B79256]/10 hover:text-[#2A2823] px-6 h-11">
          Cancel
        </Button>
      </div>
    </form>
  );
}
